import { useState, useEffect, useRef, useDeferredValue, useMemo, useCallback, memo } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Input,
  Spinner,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Link,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { glassCard } from '../styles'
import { apiFetch } from '../utils/apiFetch'
import { formatMeetingDate } from '../utils/date'
import RateModal from './RateModal'

interface LibraryBeer {
  _id: string
  name: string
  brewery?: string
  url?: string
  tastingCount: number
  avgRating: number | null
  ratingCount: number
  meetings: { _id: string; date: string }[]
}

interface MyRating {
  _id: string
  beer: { _id: string; name: string; brewery?: string; url?: string }
  meeting: { _id: string; date: string }
  score: number
  notes?: string
  createdAt: string
}

function StarRating({ score }: { score: number }) {
  return (
    <Text color="orange.300" fontSize="sm" fontWeight="semibold">
      ★ {score.toFixed(2).replace(/\.?0+$/, '')}
    </Text>
  )
}

function SortBadges<T extends string>({
  options,
  value,
  onChange,
  labels,
}: {
  options: readonly T[]
  value: T
  onChange: (v: T) => void
  labels: Record<T, string>
}) {
  return (
    <HStack spacing={2} flexWrap="wrap">
      {options.map(option => (
        <Badge
          key={option}
          cursor="pointer"
          colorScheme={value === option ? 'orange' : 'gray'}
          onClick={() => onChange(option)}
          px={2} py={1} borderRadius="md"
        >
          {labels[option]}
        </Badge>
      ))}
    </HStack>
  )
}

function EditIcon() {
  return <Text fontSize="xs">✏️</Text>
}

const CompactBeerRow = memo(function CompactBeerRow({ beer, isAdmin, onEdit }: { beer: LibraryBeer; isAdmin: boolean; onEdit: (beer: LibraryBeer) => void }) {
  return (
    <HStack justify="space-between" px={2} py={1} borderRadius="md" bg="whiteAlpha.50">
      <HStack spacing={2} flex={1} minW={0}>
        {beer.url ? (
          <Link href={beer.url} isExternal color="whiteAlpha.800" fontSize="xs"
            textDecoration="underline" _hover={{ color: 'orange.300', textDecoration: 'none' }} noOfLines={1}>
            {beer.name}
          </Link>
        ) : (
          <Text color="whiteAlpha.800" fontSize="xs" noOfLines={1}>{beer.name}</Text>
        )}
      </HStack>
      <HStack spacing={1} flexShrink={0}>
        {beer.avgRating !== null && <StarRating score={beer.avgRating} />}
        <Badge colorScheme={beer.tastingCount > 1 ? 'orange' : 'gray'} fontSize="xs">{beer.tastingCount}×</Badge>
        {isAdmin && (
          <IconButton aria-label="Edit beer" icon={<EditIcon />} size="xs" variant="ghost"
            color="whiteAlpha.400" _hover={{ color: 'white' }} onClick={e => { e.stopPropagation(); onEdit(beer) }} />
        )}
      </HStack>
    </HStack>
  )
})

const AllBeersRow = memo(function AllBeersRow({ beer, isAdmin, onEdit, onRate }: {
  beer: LibraryBeer; isAdmin: boolean; onEdit: (beer: LibraryBeer) => void; onRate?: (beer: LibraryBeer) => void
}) {
  return (
    <Box {...glassCard} borderRadius="lg" p={3}>
      <HStack justify="space-between" align="start" flexWrap="wrap" gap={2}>
        <VStack align="start" spacing={0} flex={1} minW="140px">
          {beer.url ? (
            <Link href={beer.url} isExternal color="white" fontWeight="semibold" fontSize="sm"
              textDecoration="underline" _hover={{ color: 'orange.300', textDecoration: 'none' }}>
              {beer.name}
            </Link>
          ) : (
            <Text color="white" fontWeight="semibold" fontSize="sm">{beer.name}</Text>
          )}
          {beer.brewery && <Text color="whiteAlpha.500" fontSize="xs">{beer.brewery}</Text>}
        </VStack>
        <HStack spacing={2} flexShrink={0}>
          {beer.avgRating !== null && (
            <Box cursor={onRate ? 'pointer' : undefined} onClick={onRate ? () => onRate(beer) : undefined}
              _hover={onRate ? { opacity: 0.7 } : undefined} transition="opacity 0.15s">
              <StarRating score={beer.avgRating} />
            </Box>
          )}
          <Badge colorScheme={beer.tastingCount > 1 ? 'orange' : 'gray'} fontSize="xs">
            {beer.tastingCount}×
          </Badge>
          {isAdmin && (
            <IconButton aria-label="Edit beer" icon={<EditIcon />} size="xs" variant="ghost"
              color="whiteAlpha.400" _hover={{ color: 'white' }} onClick={() => onEdit(beer)} />
          )}
        </HStack>
      </HStack>
      {beer.meetings.length > 0 && (
        <HStack flexWrap="wrap" spacing={1} mt={2} rowGap={1}>
          {beer.meetings.map(meeting => (
            <Link key={meeting._id} as={RouterLink} to={`/meetings?m=${meeting._id}`}
              fontSize="xs" color="orange.300" textDecoration="underline"
              _hover={{ color: 'orange.100' }}>
              {formatMeetingDate(meeting.date).display}
            </Link>
          ))}
        </HStack>
      )}
    </Box>
  )
})

const MyRatingRow = memo(function MyRatingRow({ rating, onEdit }: { rating: MyRating; onEdit?: (rating: MyRating) => void }) {
  return (
    <Box {...glassCard} borderRadius="lg" p={3}
      cursor={onEdit ? 'pointer' : undefined}
      onClick={() => onEdit?.(rating)}
      _hover={onEdit ? { borderColor: 'orange.400', borderWidth: '1px' } : undefined}
      transition="border-color 0.15s"
    >
      <HStack justify="space-between" align="start" flexWrap="wrap" gap={2}>
        <VStack align="start" spacing={0} flex={1} minW="140px">
          {rating.beer.url ? (
            <Link href={rating.beer.url} isExternal color="white" fontWeight="semibold"
              fontSize="sm" textDecoration="underline" _hover={{ color: 'orange.300', textDecoration: 'none' }}
              onClick={e => e.stopPropagation()}>
              {rating.beer.name}
            </Link>
          ) : (
            <Text color="white" fontWeight="semibold" fontSize="sm">{rating.beer.name}</Text>
          )}
          {rating.beer.brewery && (
            <Text color="whiteAlpha.500" fontSize="xs">{rating.beer.brewery}</Text>
          )}
          {rating.notes && (
            <Text color="whiteAlpha.600" fontSize="xs" mt={1} fontStyle="italic">"{rating.notes}"</Text>
          )}
        </VStack>
        <VStack align="end" spacing={0} flexShrink={0}>
          <StarRating score={rating.score} />
          <Link as={RouterLink} to={`/meetings?m=${rating.meeting._id}`} onClick={e => e.stopPropagation()}>
            <Text color="whiteAlpha.400" fontSize="xs" _hover={{ color: 'whiteAlpha.700' }}>
              {formatMeetingDate(rating.meeting.date).display}
            </Text>
          </Link>
        </VStack>
      </HStack>
    </Box>
  )
})

export default function BeerLibrary() {
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isRateOpen, onOpen: onRateOpen, onClose: onRateClose } = useDisclosure()
  const toast = useToast()
  const [user] = useState(() => {
    const data = localStorage.getItem('user')
    return data ? JSON.parse(data) : null
  })
  const isAdmin = user?.isAdmin === true

  const [library, setLibrary] = useState<LibraryBeer[]>([])
  const [libraryLoading, setLibraryLoading] = useState(true)
  const [myRatings, setMyRatings] = useState<MyRating[]>([])
  const [myRatingsLoading, setMyRatingsLoading] = useState(true)

  // Edit modal
  const [editingBeer, setEditingBeer] = useState<LibraryBeer | null>(null)
  const [editName, setEditName] = useState('')
  const [editBrewery, setEditBrewery] = useState('')
  const [editUrl, setEditUrl] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const editNameRef = useRef<HTMLInputElement>(null)

  // Rate modal
  const [ratingTarget, setRatingTarget] = useState<{
    beerId: string; beerName: string; brewery?: string; beerUrl?: string
    meetingId: string; meetingDate: string; meetingDateDisplay: string
    score?: number; notes?: string
  } | null>(null)
  const [rateSaving, setRateSaving] = useState(false)

  // All Beers tab
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<'count' | 'rating' | 'az'>('rating')

  // Breweries tab
  const [breweriesSearch, setBreweriesSearch] = useState('')
  const [breweriesSort, setBreweriesSort] = useState<'count' | 'rating' | 'az'>('rating')

  // My Ratings tab
  const [myRatingsSearch, setMyRatingsSearch] = useState('')
  const [myRatingsSort, setMyRatingsSort] = useState<'score-desc' | 'score-asc' | 'date' | 'az'>('score-desc')
  const [showMissed, setShowMissed] = useState(false)

  // Lazy rendering for All Beers tab
  const [allBeersVisible, setAllBeersVisible] = useState(30)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Deferred values — buttons/inputs respond immediately; list recomputes at lower priority
  const dSearch = useDeferredValue(search)
  const dSort = useDeferredValue(sort)
  const dBreweriesSearch = useDeferredValue(breweriesSearch)
  const dBreweriesSort = useDeferredValue(breweriesSort)
  const dMyRatingsSearch = useDeferredValue(myRatingsSearch)
  const dMyRatingsSort = useDeferredValue(myRatingsSort)

  const allBeersStale = search !== dSearch || sort !== dSort
  const breweriesStale = breweriesSearch !== dBreweriesSearch || breweriesSort !== dBreweriesSort
  const myRatingsStale = myRatingsSearch !== dMyRatingsSearch || myRatingsSort !== dMyRatingsSort

  useEffect(() => {
    apiFetch('/api/beers/library')
      .then(res => res.json())
      .then((data: LibraryBeer[]) => { setLibrary(data); setLibraryLoading(false) })
      .catch(() => setLibraryLoading(false))

    apiFetch('/api/beers/my-ratings')
      .then(res => res.json())
      .then((data: MyRating[]) => { setMyRatings(data); setMyRatingsLoading(false) })
      .catch(() => setMyRatingsLoading(false))
  }, [])

  // Reset visible count when search/sort changes
  useEffect(() => { setAllBeersVisible(30) }, [search, sort])

  // Load more when scrolling near the bottom
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) setAllBeersVisible(prev => prev + 30) },
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [libraryLoading])

  const openEdit = useCallback((beer: LibraryBeer) => {
    setEditingBeer(beer)
    setEditName(beer.name)
    setEditBrewery(beer.brewery ?? '')
    setEditUrl(beer.url ?? '')
    onEditOpen()
  }, [onEditOpen])

  async function saveEdit() {
    if (!editingBeer) return
    setEditSaving(true)
    try {
      const res = await apiFetch(`/api/beers/${editingBeer._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName, brewery: editBrewery, url: editUrl }),
      })
      if (res.ok) {
        const updated = await res.json()
        setLibrary(prev => prev.map(beer => beer._id === editingBeer._id
          ? { ...beer, name: updated.name, brewery: updated.brewery ?? undefined, url: updated.url ?? undefined }
          : beer
        ))
        onEditClose()
        toast({ status: 'success', description: 'Beer updated', duration: 2000, isClosable: true })
      } else {
        const errorBody = await res.json().catch(() => ({}))
        toast({
          status: 'error',
          title: `Save failed (${res.status})`,
          description: (errorBody as { error?: string }).error ?? 'Unknown error',
          duration: 5000,
          isClosable: true,
        })
      }
    } catch (err) {
      toast({ status: 'error', title: 'Network error', description: String(err), duration: 5000, isClosable: true })
    } finally {
      setEditSaving(false)
    }
  }

  const openRateModal = useCallback((target: typeof ratingTarget) => {
    setRatingTarget(target)
    onRateOpen()
  }, [onRateOpen])

  const handleRateFromLibrary = useCallback((beer: LibraryBeer) => {
    const existing = myRatings.find(r => r.beer._id === beer._id)
    if (existing) {
      openRateModal({
        beerId: existing.beer._id,
        beerName: existing.beer.name,
        brewery: existing.beer.brewery,
        beerUrl: existing.beer.url,
        meetingId: existing.meeting._id,
        meetingDate: existing.meeting.date,
        meetingDateDisplay: formatMeetingDate(existing.meeting.date).display,
        score: existing.score,
        notes: existing.notes,
      })
    } else if (beer.meetings.length > 0) {
      const meeting = beer.meetings[0] // most recent (already sorted desc)
      openRateModal({
        beerId: beer._id,
        beerName: beer.name,
        brewery: beer.brewery,
        beerUrl: beer.url,
        meetingId: meeting._id,
        meetingDate: meeting.date,
        meetingDateDisplay: formatMeetingDate(meeting.date).display,
      })
    }
  }, [myRatings, openRateModal])

  const handleEditRating = useCallback((rating: MyRating) => {
    openRateModal({
      beerId: rating.beer._id,
      beerName: rating.beer.name,
      brewery: rating.beer.brewery,
      beerUrl: rating.beer.url,
      meetingId: rating.meeting._id,
      meetingDate: rating.meeting.date,
      meetingDateDisplay: formatMeetingDate(rating.meeting.date).display,
      score: rating.score,
      notes: rating.notes,
    })
  }, [openRateModal])

  async function handleSaveRating(score: number, notes: string) {
    if (!ratingTarget) return
    setRateSaving(true)
    try {
      const res = await apiFetch(`/api/beers/${ratingTarget.beerId}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meetingId: ratingTarget.meetingId, score, notes: notes || undefined }),
      })
      if (res.ok) {
        const saved = await res.json()
        if (ratingTarget.score !== undefined) {
          setMyRatings(prev => prev.map(r =>
            (r.beer._id === ratingTarget.beerId && r.meeting._id === ratingTarget.meetingId)
              ? { ...r, score: saved.score, notes: saved.notes }
              : r
          ))
        } else {
          const newRating: MyRating = {
            _id: saved._id,
            beer: { _id: ratingTarget.beerId, name: ratingTarget.beerName, brewery: ratingTarget.brewery, url: ratingTarget.beerUrl },
            meeting: { _id: ratingTarget.meetingId, date: ratingTarget.meetingDate },
            score: saved.score,
            notes: saved.notes,
            createdAt: saved.createdAt,
          }
          setMyRatings(prev => [newRating, ...prev])
        }
        onRateClose()
        toast({ status: 'success', description: ratingTarget.score !== undefined ? 'Rating updated' : 'Rating saved', duration: 2000, isClosable: true })
      } else {
        const errorBody = await res.json().catch(() => ({}))
        toast({ status: 'error', title: `Save failed (${res.status})`, description: (errorBody as { error?: string }).error ?? 'Unknown error', duration: 5000, isClosable: true })
      }
    } catch (err) {
      toast({ status: 'error', title: 'Network error', description: String(err), duration: 5000, isClosable: true })
    } finally {
      setRateSaving(false)
    }
  }

  async function handleClearRating() {
    if (!ratingTarget) return
    setRateSaving(true)
    try {
      const res = await apiFetch(`/api/beers/${ratingTarget.beerId}/rate`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meetingId: ratingTarget.meetingId }),
      })
      if (res.ok) {
        setMyRatings(prev => prev.filter(r =>
          !(r.beer._id === ratingTarget.beerId && r.meeting._id === ratingTarget.meetingId)
        ))
        onRateClose()
        toast({ status: 'success', description: 'Rating cleared', duration: 2000, isClosable: true })
      } else {
        const errorBody = await res.json().catch(() => ({}))
        toast({ status: 'error', title: `Delete failed (${res.status})`, description: (errorBody as { error?: string }).error ?? 'Unknown error', duration: 5000, isClosable: true })
      }
    } catch (err) {
      toast({ status: 'error', title: 'Network error', description: String(err), duration: 5000, isClosable: true })
    } finally {
      setRateSaving(false)
    }
  }

  // All Beers — memoized so urgent renders (button highlights) skip recomputation
  const filteredBeers = useMemo(() =>
    library
      .filter(beer => {
        if (!dSearch) return true
        const query = dSearch.toLowerCase()
        return beer.name.toLowerCase().includes(query) || beer.brewery?.toLowerCase().includes(query)
      })
      .sort((a, b) => {
        if (dSort === 'count') return b.tastingCount - a.tastingCount
        if (dSort === 'rating') {
          if (a.avgRating === null && b.avgRating === null) return 0
          if (a.avgRating === null) return 1
          if (b.avgRating === null) return -1
          return b.avgRating - a.avgRating
        }
        return a.name.localeCompare(b.name)
      }),
    [library, dSearch, dSort]
  )

  const { totalTastings, overallAvg } = useMemo(() => {
    const total = library.reduce((sum, beer) => sum + beer.tastingCount, 0)
    const rated = library.filter(beer => beer.avgRating !== null)
    const avg = rated.length > 0 ? rated.reduce((sum, beer) => sum + beer.avgRating!, 0) / rated.length : null
    return { totalTastings: total, overallAvg: avg }
  }, [library])

  // Breweries
  const { breweryMap, breweries } = useMemo(() => {
    const map = new Map<string, { beerList: LibraryBeer[]; tastings: number }>()
    for (const beer of library) {
      const key = beer.brewery ?? 'Unknown brewery'
      const existing = map.get(key) ?? { beerList: [], tastings: 0 }
      map.set(key, { beerList: [...existing.beerList, beer], tastings: existing.tastings + beer.tastingCount })
    }
    const list = Array.from(map.entries())
      .map(([name, { beerList, tastings }]) => {
        const ratedList = beerList.filter(beer => beer.avgRating !== null)
        const avgRating = ratedList.length > 0
          ? ratedList.reduce((sum, beer) => sum + beer.avgRating!, 0) / ratedList.length
          : null
        return { name, beerList: beerList.sort((a, b) => a.name.localeCompare(b.name)), tastings, avgRating }
      })
      .filter(brewery => {
        if (!dBreweriesSearch) return true
        const query = dBreweriesSearch.toLowerCase()
        return brewery.name.toLowerCase().includes(query) || brewery.beerList.some(beer => beer.name.toLowerCase().includes(query))
      })
      .sort((a, b) => {
        if (dBreweriesSort === 'count') return b.tastings - a.tastings
        if (dBreweriesSort === 'rating') {
          if (a.avgRating === null && b.avgRating === null) return 0
          if (a.avgRating === null) return 1
          if (b.avgRating === null) return -1
          return b.avgRating - a.avgRating
        }
        return a.name.localeCompare(b.name)
      })
    return { breweryMap: map, breweries: list }
  }, [library, dBreweriesSearch, dBreweriesSort])

  // My Ratings — compute "unrated" beers: attended a meeting with that beer but never rated it at all
  const missedRatings = useMemo(() => {
    if (myRatings.length === 0) return []
    const attendedMeetingIds = new Set(myRatings.map(rating => rating.meeting._id))
    const ratedBeerIds = new Set(myRatings.map(rating => rating.beer._id))
    return library
      .filter(beer => !ratedBeerIds.has(beer._id) && beer.meetings.some(m => attendedMeetingIds.has(m._id)))
      .map(beer => ({
        beer,
        meeting: beer.meetings
          .filter(m => attendedMeetingIds.has(m._id))
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0],
      }))
      .sort((a, b) => new Date(b.meeting.date).getTime() - new Date(a.meeting.date).getTime())
  }, [library, myRatings])

  const filteredMissedRatings = useMemo(() => {
    if (!dMyRatingsSearch) return missedRatings
    const query = dMyRatingsSearch.toLowerCase()
    return missedRatings.filter(m => m.beer.name.toLowerCase().includes(query) || m.beer.brewery?.toLowerCase().includes(query))
  }, [missedRatings, dMyRatingsSearch])

  const filteredMyRatings = useMemo(() =>
    myRatings
      .filter(rating => {
        if (!dMyRatingsSearch) return true
        const query = dMyRatingsSearch.toLowerCase()
        return rating.beer.name.toLowerCase().includes(query) || rating.beer.brewery?.toLowerCase().includes(query)
      })
      .sort((a, b) => {
        if (dMyRatingsSort === 'score-desc') return b.score - a.score
        if (dMyRatingsSort === 'score-asc')  return a.score - b.score
        if (dMyRatingsSort === 'date')       return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        return a.beer.name.localeCompare(b.beer.name)
      }),
    [myRatings, dMyRatingsSearch, dMyRatingsSort]
  )

  const myRatingsStats = useMemo(() => {
    if (myRatings.length === 0) return null
    const uniqueBeerCount = new Set(myRatings.map(rating => rating.beer._id)).size + missedRatings.length
    const avgRating = myRatings.reduce((sum, rating) => sum + rating.score, 0) / myRatings.length
    return { uniqueBeerCount, avgRating }
  }, [myRatings, missedRatings])

  return (
    <Box minH="100vh" position="relative" p={{ base: 4, md: 8 }} pt={{ base: 16, md: 20 }}>

      <Box maxW="900px" mx="auto">
        <VStack spacing={6} align="stretch">
          <Text fontSize="2xl" fontWeight="bold" color="white">🍺 Beer Library</Text>

          <Tabs colorScheme="orange" variant="soft-rounded">
            <TabList>
              <Tab color="whiteAlpha.700" _selected={{ color: 'white', bg: 'orange.500' }}>All Beers</Tab>
              <Tab color="whiteAlpha.700" _selected={{ color: 'white', bg: 'orange.500' }}>Breweries</Tab>
              <Tab color="whiteAlpha.700" _selected={{ color: 'white', bg: 'orange.500' }}>
                My Ratings
                {!myRatingsLoading && missedRatings.length > 0 && (
                  <Badge ml={1} colorScheme="red" borderRadius="full" fontSize="xs">{missedRatings.length}</Badge>
                )}
              </Tab>
            </TabList>

            <TabPanels>
              {/* All Beers tab */}
              <TabPanel px={0} pt={5}>
                {libraryLoading ? (
                  <Spinner color="white" />
                ) : (
                  <VStack spacing={3} align="stretch">
                    <SimpleGrid columns={3} spacing={4}>
                      {[
                        { value: library.length, label: 'Unique beers' },
                        { value: totalTastings, label: 'Total tastings' },
                        { value: overallAvg ? `★ ${overallAvg.toFixed(2).replace(/\.?0+$/, '')}` : '—', label: 'Avg rating' },
                      ].map(({ value, label }) => (
                        <Box key={label} {...glassCard} p={4} borderRadius="lg" textAlign="center">
                          <Text fontSize="xl" fontWeight="bold" color="white">{value}</Text>
                          <Text fontSize="xs" color="whiteAlpha.600">{label}</Text>
                        </Box>
                      ))}
                    </SimpleGrid>

                    <Input
                      placeholder="Search beers or breweries…"
                      value={search}
                      onChange={event => setSearch(event.target.value)}
                      color="white" borderColor="whiteAlpha.300"
                      _placeholder={{ color: 'whiteAlpha.400' }}
                    />
                    <SortBadges
                      options={['rating', 'count', 'az'] as const}
                      value={sort} onChange={setSort}
                      labels={{ rating: 'Highest rated', count: 'Most tasted', az: 'A–Z' }}
                    />

                    <VStack spacing={2} align="stretch" opacity={allBeersStale ? 0.6 : 1} transition="opacity 0.15s">
                      {filteredBeers.slice(0, allBeersVisible).map(beer => (
                        <AllBeersRow key={beer._id} beer={beer} isAdmin={isAdmin} onEdit={openEdit} onRate={handleRateFromLibrary} />
                      ))}
                      {filteredBeers.length === 0 && (
                        <Text color="whiteAlpha.500" fontSize="sm" fontStyle="italic">No beers found.</Text>
                      )}
                      <Box ref={sentinelRef} />
                    </VStack>
                  </VStack>
                )}
              </TabPanel>

              {/* Breweries tab */}
              <TabPanel px={0} pt={5}>
                {libraryLoading ? (
                  <Spinner color="white" />
                ) : (
                  <VStack spacing={3} align="stretch">
                    <SimpleGrid columns={3} spacing={4}>
                      {[
                        { value: breweryMap.size, label: 'Breweries' },
                        { value: library.length, label: 'Unique beers' },
                        { value: overallAvg ? `★ ${overallAvg.toFixed(2).replace(/\.?0+$/, '')}` : '—', label: 'Avg rating' },
                      ].map(({ value, label }) => (
                        <Box key={label} {...glassCard} p={4} borderRadius="lg" textAlign="center">
                          <Text fontSize="xl" fontWeight="bold" color="white">{value}</Text>
                          <Text fontSize="xs" color="whiteAlpha.600">{label}</Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                    <Input
                      placeholder="Search breweries or beers…"
                      value={breweriesSearch}
                      onChange={event => setBreweriesSearch(event.target.value)}
                      color="white" borderColor="whiteAlpha.300"
                      _placeholder={{ color: 'whiteAlpha.400' }}
                    />
                    <SortBadges
                      options={['rating', 'count', 'az'] as const}
                      value={breweriesSort} onChange={setBreweriesSort}
                      labels={{ rating: 'Highest rated', count: 'Most tasted', az: 'A–Z' }}
                    />
                    <Box opacity={breweriesStale ? 0.6 : 1} transition="opacity 0.15s">
                    {breweries.map(brewery => (
                      <Box key={brewery.name} {...glassCard} borderRadius="lg" p={3} mb={3}>
                        <HStack justify="space-between" mb={2}>
                          <HStack spacing={3}>
                            <Text color="white" fontWeight="semibold" fontSize="sm">{brewery.name}</Text>
                            {brewery.avgRating !== null && <StarRating score={brewery.avgRating} />}
                          </HStack>
                          <Text color="whiteAlpha.500" fontSize="xs">
                            {brewery.beerList.length} beer{brewery.beerList.length !== 1 ? 's' : ''} · {brewery.tastings} tasting{brewery.tastings !== 1 ? 's' : ''}
                          </Text>
                        </HStack>
                        <VStack spacing={1} align="stretch">
                          {brewery.beerList.map(beer => <CompactBeerRow key={beer._id} beer={beer} isAdmin={isAdmin} onEdit={openEdit} />)}
                        </VStack>
                      </Box>
                    ))}
                    {breweries.length === 0 && (
                      <Text color="whiteAlpha.500" fontSize="sm" fontStyle="italic">No breweries found.</Text>
                    )}
                    </Box>
                  </VStack>
                )}
              </TabPanel>

              {/* My Ratings tab */}
              <TabPanel px={0} pt={5}>
                {myRatingsLoading ? (
                  <Spinner color="white" />
                ) : myRatings.length === 0 ? (
                  <Text color="whiteAlpha.500" fontSize="sm" fontStyle="italic">
                    You haven't rated any beers yet.
                  </Text>
                ) : (
                  <VStack spacing={3} align="stretch">
                    {myRatingsStats && (
                      <SimpleGrid columns={3} spacing={4}>
                        {[
                          { value: myRatings.length, label: 'Ratings given' },
                          { value: myRatingsStats.uniqueBeerCount, label: 'Unique beers' },
                          { value: `★ ${myRatingsStats.avgRating.toFixed(2).replace(/\.?0+$/, '')}`, label: 'Avg rating' },
                        ].map(({ value, label }) => (
                          <Box key={label} {...glassCard} p={4} borderRadius="lg" textAlign="center">
                            <Text fontSize="xl" fontWeight="bold" color="white">{value}</Text>
                            <Text fontSize="xs" color="whiteAlpha.600">{label}</Text>
                          </Box>
                        ))}
                      </SimpleGrid>
                    )}

                    {/* Search */}
                    <Input
                      placeholder="Search beers or breweries…"
                      value={myRatingsSearch}
                      onChange={event => setMyRatingsSearch(event.target.value)}
                      color="white" borderColor="whiteAlpha.300"
                      _placeholder={{ color: 'whiteAlpha.400' }}
                    />
                    {/* Toggle */}
                    <HStack spacing={2}>
                      <Badge cursor="pointer" colorScheme={!showMissed ? 'orange' : 'gray'}
                        onClick={() => setShowMissed(false)} px={2} py={1} borderRadius="md">
                        My Ratings
                      </Badge>
                      <Badge cursor="pointer" colorScheme={showMissed ? 'orange' : 'gray'}
                        onClick={() => setShowMissed(true)} px={2} py={1} borderRadius="md">
                        Unrated ({missedRatings.length})
                      </Badge>
                    </HStack>

                    {showMissed ? (
                      /* Unrated / missed view */
                      filteredMissedRatings.length === 0 ? (
                        <Text color="whiteAlpha.500" fontSize="sm" fontStyle="italic">
                          {myRatingsSearch ? 'No unrated beers match your search.' : 'All caught up — no unrated beers from meetings you\'ve attended.'}
                        </Text>
                      ) : (
                        <VStack spacing={2} align="stretch">
                          {filteredMissedRatings.map((item, idx) => (
                            <Box key={`${item.meeting._id}-${item.beer._id}-${idx}`} {...glassCard} borderRadius="lg" p={3}
                              cursor="pointer"
                              onClick={() => openRateModal({
                                beerId: item.beer._id,
                                beerName: item.beer.name,
                                brewery: item.beer.brewery,
                                beerUrl: item.beer.url,
                                meetingId: item.meeting._id,
                                meetingDate: item.meeting.date,
                                meetingDateDisplay: formatMeetingDate(item.meeting.date).display,
                              })}
                              _hover={{ borderColor: 'orange.400', borderWidth: '1px' }}
                              transition="border-color 0.15s"
                            >
                              <HStack justify="space-between" align="start" flexWrap="wrap" gap={2}>
                                <VStack align="start" spacing={0} flex={1} minW="140px">
                                  {item.beer.url ? (
                                    <Link href={item.beer.url} isExternal color="white" fontWeight="semibold"
                                      fontSize="sm" textDecoration="underline" _hover={{ color: 'orange.300', textDecoration: 'none' }}
                                      onClick={e => e.stopPropagation()}>
                                      {item.beer.name}
                                    </Link>
                                  ) : (
                                    <Text color="white" fontWeight="semibold" fontSize="sm">{item.beer.name}</Text>
                                  )}
                                  {item.beer.brewery && <Text color="whiteAlpha.500" fontSize="xs">{item.beer.brewery}</Text>}
                                </VStack>
                                <HStack spacing={2} flexShrink={0}>
                                  <Badge colorScheme="orange" fontSize="xs">Rate</Badge>
                                  <Link as={RouterLink} to={`/meetings?m=${item.meeting._id}`} flexShrink={0} onClick={e => e.stopPropagation()}>
                                    <Text color="whiteAlpha.400" fontSize="xs" _hover={{ color: 'whiteAlpha.700' }}>
                                      {formatMeetingDate(item.meeting.date).display}
                                    </Text>
                                  </Link>
                                </HStack>
                              </HStack>
                            </Box>
                          ))}
                        </VStack>
                      )
                    ) : (
                      /* Rated view */
                      <VStack spacing={3} align="stretch">
                        <SortBadges
                          options={['score-desc', 'score-asc', 'date', 'az'] as const}
                          value={myRatingsSort} onChange={setMyRatingsSort}
                          labels={{ 'score-desc': 'Highest rated', 'score-asc': 'Lowest rated', date: 'Most recent', az: 'A–Z' }}
                        />
                        <VStack spacing={2} align="stretch" opacity={myRatingsStale ? 0.6 : 1} transition="opacity 0.15s">
                          {filteredMyRatings.map(rating => (
                            <MyRatingRow key={rating._id} rating={rating} onEdit={handleEditRating} />
                          ))}
                          {filteredMyRatings.length === 0 && (
                            <Text color="whiteAlpha.500" fontSize="sm" fontStyle="italic">No ratings match your search.</Text>
                          )}
                        </VStack>
                      </VStack>
                    )}
                  </VStack>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Box>

      {/* Rate/edit rating modal */}
      <RateModal
        isOpen={isRateOpen}
        onClose={onRateClose}
        beerName={ratingTarget?.beerName ?? ''}
        brewery={ratingTarget?.brewery}
        meetingDateDisplay={ratingTarget?.meetingDateDisplay ?? ''}
        initialScore={ratingTarget?.score}
        initialNotes={ratingTarget?.notes}
        saving={rateSaving}
        onSave={handleSaveRating}
        onClear={handleClearRating}
      />

      {/* Edit beer modal (admin only) */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} initialFocusRef={editNameRef}>
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>Edit Beer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input ref={editNameRef} value={editName} onChange={e => setEditName(e.target.value)}
                  borderColor="whiteAlpha.300" _placeholder={{ color: 'whiteAlpha.400' }} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Brewery</FormLabel>
                <Input value={editBrewery} onChange={e => setEditBrewery(e.target.value)}
                  placeholder="Leave empty to clear"
                  borderColor="whiteAlpha.300" _placeholder={{ color: 'whiteAlpha.400' }} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">URL</FormLabel>
                <Input value={editUrl} onChange={e => setEditUrl(e.target.value)}
                  placeholder="Leave empty to clear"
                  borderColor="whiteAlpha.300" _placeholder={{ color: 'whiteAlpha.400' }} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose} color="whiteAlpha.700">Cancel</Button>
            <Button colorScheme="orange" onClick={saveEdit} isLoading={editSaving}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
